import { Clock, Users2, CheckCircle2 } from "lucide-react";
import { useSiteAssetSingle } from "@/hooks/useSiteAssets";

/**
 * Renders the founder introduction video only when an active asset exists
 * in `site_assets` (role = founder_video). No placeholders.
 */
const FounderVideo = () => {
  const { asset: videoAsset } = useSiteAssetSingle("founder_video");
  const { asset: posterAsset } = useSiteAssetSingle("founder_video_poster");
  if (!videoAsset) return null;
  const url = videoAsset.file_url;
  const posterUrl = posterAsset?.file_url || null;
  const isYouTube = /youtube\.com|youtu\.be/.test(url);
  const isVimeo = /vimeo\.com/.test(url);
  const isEmbed = isYouTube || isVimeo;

  const embedSrc = (() => {
    if (isYouTube) {
      const id = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{6,})/)?.[1];
      return id ? `https://www.youtube.com/embed/${id}` : url;
    }
    if (isVimeo) {
      const id = url.match(/vimeo\.com\/(\d+)/)?.[1];
      return id ? `https://player.vimeo.com/video/${id}` : url;
    }
    return url;
  })();

  return (
    <section className="section-padding bg-muted/40">
      <div className="container-narrow">
        <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
          <div>
            <div className="gold-divider" />
            <h2 className="mt-4 font-display text-3xl font-bold text-foreground md:text-4xl">
              A short message from our founder
            </h2>
            <p className="mt-4 text-muted-foreground">
              Hear directly from the operator behind Elevate, how we built our acquisition teams,
              what we believe, and exactly what you should expect from your first 90 days with us.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-foreground">
              <li className="flex items-start gap-2.5">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
                Who we are and who we work with
              </li>
              <li className="flex items-start gap-2.5">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
                How we train acquisition talent in Egypt for US REI
              </li>
              <li className="flex items-start gap-2.5">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
                What the first 14 days look like
              </li>
            </ul>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-xs uppercase tracking-wider text-muted-foreground">
              <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-secondary" /> Short watch</span>
              <span className="flex items-center gap-1.5"><Users2 className="h-3.5 w-3.5 text-secondary" /> Founder direct</span>
            </div>
          </div>

          <div className="relative aspect-video overflow-hidden rounded-2xl border border-border shadow-xl shadow-primary/10 bg-primary">
            {isEmbed ? (
              <iframe
                className="h-full w-full"
                src={embedSrc}
                title="Founder introduction"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video
                className="h-full w-full"
                src={url}
                poster={posterUrl || undefined}
                controls
                playsInline
                preload="metadata"
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FounderVideo;
